import { MissionData } from '../types';
import { useEffect } from 'react';
import { UploadData, useUploadStore } from '../../../../store/uploadStore';
import getPilotApiService from '../../../../api_services/PilotApiService';
import { UploadType } from '../../../../consts/uploadType';
import { cacheUploadSize } from '../../../../store/optimizations/cacheUploadSize';
import { cacheUploadedSize } from '../../../../store/optimizations/cacheUploadedSize';
import { resetUploadSpeedMeasurement } from '../../../../modules/intakeLoop/upload/measurements/useMeasureUploadSpeed';

const getMaxPhotoPrefix = (upload: UploadData) => {
  if (!upload.upload?.photos) {
    return -1;
  }

  let maxPrefix = -1;

  for (const photo of upload.upload.photos) {
    const parts = photo.name.split('/');
    if (parts.length < 2) {
      continue;
    }

    const prefix = parts[0];

    // Check if the prefix is numeric
    if (/^\d+$/.test(prefix)) {
      const numericPrefix = parseInt(prefix, 10);
      maxPrefix = Math.max(maxPrefix, numericPrefix);
    }
  }

  return maxPrefix;
};

export const useLoadMissionUploads = (selectedMission: MissionData | null) => {
  const { getUpload, setUpload } = useUploadStore();

  const scanUpload = selectedMission
    ? getUpload(false, selectedMission.customer.id, selectedMission.site.id, selectedMission.schedule?.airtableRecordId)
    : null;

  const panoUpload = selectedMission
    ? getUpload(true, selectedMission.customer.id, selectedMission.site.id, selectedMission.schedule?.airtableRecordId)
    : null;

  useEffect(() => {
    if (!selectedMission) return;
    if (!selectedMission.site.hasUploadPermission) return;

    const restoreUpload = async (upload?: UploadData | null) => {
      if (!upload || !upload.overview || upload.upload || upload.meta.restoringUpload) return;

      const isPano = upload.meta.type === UploadType.Pano;

      upload.meta.restoringUpload = true;
      setUpload(
        isPano,
        selectedMission.customer.id,
        selectedMission.site.id,
        selectedMission.schedule?.airtableRecordId,
        { ...upload },
      );

      // Fetch upload data
      const data = await getPilotApiService().getIntakeFlow(
        selectedMission.customer.id,
        selectedMission.site.id,
        upload.meta.type,
        upload.overview.date,
        upload.overview.flightScheduleId,
      );

      // Update with fetched data
      upload.upload = data;
      upload.meta.restoringUpload = false;
      upload.meta.currentBatch = getMaxPhotoPrefix(upload);
      cacheUploadedSize(upload);
      cacheUploadSize(upload);
      setUpload(
        isPano,
        selectedMission.customer.id,
        selectedMission.site.id,
        selectedMission.schedule?.airtableRecordId,
        { ...upload },
      );

      if (upload.upload.photos.length) {
        resetUploadSpeedMeasurement();
      }
    };

    // Restore both scan and pano
    restoreUpload(scanUpload);
    restoreUpload(panoUpload);
  }, [scanUpload, panoUpload, selectedMission, setUpload]);
};
